import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useReactToPrint } from 'react-to-print';
import WalkInSection from './WalkInSection.js';
import TablesSection from './TablesSection.js';
import SalesWaiterSection from './SalesWaiterSection.js';
import PrintContent from '../common/PrintContent.js';
import useAppStore from '../../store/useAppStore.js';
import { useOrderStore } from '../../store/branchstore/branchOrdersStore.js';
import { fetchInventory, fetchProducts, submitSale } from '../../services/inventoryApi.js';
import * as offlineDb from '../../services/offlineDb.js';
import { getWaiters } from '../../services/waiterApi.js';
import { fetchTables } from '../../services/tableApi.js';
import { createOrder, appendOrderItems, updateOrderItems, completeOrder, getOrderById, createReceipt, createToken, getOrders } from '../../services/orderApi.js';
import { updateTokenStatus } from '../../services/tokenApi.js';

const Sales = () => {
  const {
    activeTab,
    category,
    selectedTable,
    selectedPending,
    receiptItems,
    discount,
    receivedCash,
    branchTables,
    user,
    branchReceiptData,
    selectedBranch,
    offlineMode,
    editingOrder,
    clearEditingOrder,
    // Product store
    products,
    productHasLoaded,
    initializeProducts,
    setProducts,
    // Inventory store
    inventoryItems,
    inventoryHasLoaded,
    initializeInventory,
    // Sales actions
    setActiveTab,
    setSelectedTable,
    addReceiptItem,
    addReceiptItemsToTable,
    cancelTableOrder,
    removeTable,
    createTable,
    activateTable,
    clearReceipt,
    setTables,
    setDiscount,
    loadTables
  } = useAppStore((state) => ({
    activeTab: state.activeTab,
    category: state.category,
    selectedTable: state.selectedTable,
    selectedPending: state.selectedPending,
    receiptItems: state.receiptItems,
    discount: state.discount,
    receivedCash: state.receivedCash,
    branchTables: state.branchTables,
    user: state.user,
    branchReceiptData: state.branchReceiptData,
    selectedBranch: state.selectedBranch,
    offlineMode: state.offlineMode,
    editingOrder: state.editingOrder,
    clearEditingOrder: state.clearEditingOrder,
    // Product store
    products: state.products,
    productHasLoaded: state.productHasLoaded,
    initializeProducts: state.initializeProducts,
    setProducts: state.setProducts,
    // Inventory store
    inventoryItems: state.inventoryItems,
    inventoryHasLoaded: state.inventoryHasLoaded,
    initializeInventory: state.initializeInventory,
    // Sales actions
    setActiveTab: state.setActiveTab,
    setSelectedTable: state.setSelectedTable,
    addReceiptItem: state.addReceiptItem,
    addReceiptItemsToTable: state.addReceiptItemsToTable,
    cancelTableOrder: state.cancelTableOrder,
    removeTable: state.removeTable,
    createTable: state.createTable,
    activateTable: state.activateTable,
    clearReceipt: state.clearReceipt,
    setTables: state.setTables,
    setDiscount: state.setDiscount,
    loadTables: state.loadTables
  }));

  // Get order store actions
  const { addOrder } = useOrderStore((state) => ({
    addOrder: state.addOrder
  }));

  const [waiterId, setWaiterId] = useState('');
  const [waiters, setWaiters] = useState([]);
  const [waiterOrders, setWaiterOrders] = useState([]);
  const [selectedWaiterId, setSelectedWaiterId] = useState('');
  const [selectedWaiterOrderId, setSelectedWaiterOrderId] = useState('');
  const [saleMessage, setSaleMessage] = useState('');
  const [saleError, setSaleError] = useState('');
  const [createdToken, setCreatedToken] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const groupItemsByTokenPaper = (items) => {
    return items.reduce((groups, item) => {
      const paper = Number(item.token_paper || 1);
      if (!groups[paper]) {
        groups[paper] = [];
      }
      groups[paper].push(item);
      return groups;
    }, {});
  };

  // Print state management
  const [printMode, setPrintMode] = useState('both'); // 'receipt', 'token', or 'both'
  const [currentPrintOrder, setCurrentPrintOrder] = useState(null);
  const [isPrinting, setIsPrinting] = useState(false);
  const [isPrintPending, setIsPrintPending] = useState(false);

  // Single ref for the unified PrintContent component
  const printRef = useRef();

  // Thermal printer styles (80mm width)
  const thermalPrinterStyle = `
    @page {
      size: 80mm auto;
      margin: 0;
    }
    body {
      margin: 0;
      padding: 0;
      width: 80mm;
    }
  `;

  /**
   * Normalize order data into consistent structure for printing
   * Ensures all required fields are present and properly formatted
   */
  const normalizeOrderData = (order) => {
    const selectedTableObj = (branchTables || []).find((table) => table.id === order.tableId );

    const tableName = order?.name ||  order.table?.name || selectedTableObj?.label || selectedTableObj?.name || selectedTableObj?.tableName || 'Walk-in';
    const tableId = order.table?.id || order.tableId || order.table_id || selectedTableObj?.id || selectedTableObj?.tableId || null;

    return {
      id: order.id || order.orderId,
      order_number: order.order_number || null,
      items: (order.items || []).map((item) => ({
        id: item.id || Math.random().toString(36).substr(2, 9),
        name: item.name || item.product_name || item.variant_name || '',
        price: Number(item.price ?? item.unit_price) || 0,
        quantity: Number(item.quantity) || 0
      })),

      subtotal: Number(order.subtotal) || 0,
      discount: Number(order.discount) || 0,
      total: Number(order.total) || 0,
      table: tableName ? {
        name: tableName,
        id: tableId
      } : null,
      waiter_id: order.waiter_id || order.waiterId || order.waiter || null,
      waiter_name: order.waiter_name || order.waiterName || null,
      token: order.token || (order.token_number ? { token_no: order.token_number } : null),
      receipt: order.receipt || null,
      order_type: order.order_type || null,
      createdAt: order.createdAt || order.created_at || new Date().toLocaleString()
    };
  };

  const loadOrderPrintData = async (order) => {
    if (!order) return null;

    const orderId = order.order_id || order.id;
    const needsFetch = !order.receipt || !order.token || !Array.isArray(order.items) || order.items.length === 0;
    if (!orderId || !needsFetch) {
      return order;
    }

    try {
      const response = await getOrderById(orderId);
      let orderDetails = response.order || {};
      const items = (orderDetails.items || order.items || []).map((item) => ({
        id: item.id || Math.random().toString(36).substr(2, 9),
        name: item.product_name || item.variant_name || item.name || '',
        price: Number(item.unit_price ?? item.price) || 0,
        quantity: Number(item.quantity) || 0
      }));

      // Create receipt if it doesn't exist
      if (!orderDetails.receipt) {
        try {
          const receiptResponse = await createReceipt(orderId);
          orderDetails.receipt = receiptResponse.receipt;
        } catch (receiptError) {
          console.error('Error creating receipt:', receiptError);
          // Continue without receipt for now
        }
      }

      return {
        ...order,
        ...orderDetails,
        items,
        token: orderDetails.token || (orderDetails.token_number ? { token_no: orderDetails.token_number } : order.token),
        receipt: orderDetails.receipt || order.receipt,
        table: orderDetails.table || order.table ||
          (orderDetails.table_name ? { name: orderDetails.table_name } : (orderDetails.table_id ? { id: orderDetails.table_id } : null)) ||
          (order.name ? { name: order.name, id: order.table_id || order.order_id || null } : null),
        tableId: orderDetails.tableId || orderDetails.table_id || order.tableId || order.table_id || null,
        createdAt: orderDetails.created_at || order.createdAt,
        order_type: orderDetails.order_type || order.order_type
      };
    } catch (error) {
      console.error('Error loading print data for order', error);
      return order;
    }
  };

  // Centralized print handler using react-to-print
  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Print-${new Date().getTime()}`,
    pageStyle: thermalPrinterStyle,
    onAfterPrint: () => {
      setIsPrinting(false);
      setIsPrintPending(false);
      // Reset print mode to default after token-only printing
      setPrintMode('both');
      // Clear receipt and refresh data after printing completes
      clearReceipt();
      refreshData();
      if (currentPrintOrder?.completeAfterPrint && currentPrintOrder.orderId) {
        completeTableOrder(currentPrintOrder.orderId);
      }
      setCurrentPrintOrder(null);
      setSaleMessage('');
    }
  });


  const pendingOptions = useMemo(
    () => [
      { value: 'P001', label: 'Pending A-102', items: [{ id: 'PRD-002', name: 'Double Scoop Cup', price: 6.8, quantity: 1 }, { id: 'PRD-004', name: 'Chocolate Dipped Cone', price: 5.5, quantity: 1 }] },
      { value: 'P002', label: 'Pending B-301', items: [{ id: 'PRD-003', name: 'Family Sundae', price: 18.0, quantity: 1 }] }
    ],
    []
  );

  const loadData = async () => {
    try {
      if (offlineMode) {
        const productsData = await offlineDb.getOfflineProducts();
        setProducts(productsData || []);
        return;
      }

      await Promise.all([
        initializeProducts(fetchProducts),
        initializeInventory(fetchInventory)
      ]);
    } catch (error) {
      setSaleError(error.message || 'Unable to load sale data.');
    }
  };

  const loadWaiters = async () => {
    try {
      if (offlineMode) {
        const offlineWaiters = await offlineDb.getOfflineWaiters();
        setWaiters(offlineWaiters || []);
        return;
      }

      const response = await getWaiters();
      setWaiters(response.waiters || []);
    } catch (error) {
      console.error('Error loading waiters:', error);
    }
  };

  const loadWaiterOrders = async () => {
    if (offlineMode) return;
    try {
      const response = await getOrders();
      const orders = response.orders || [];
      const today = new Date().toDateString();
      const waiterOrders = orders.filter((order) => {
        const orderDate = new Date(order.created_at).toDateString();
        return (
          order.order_type === 'waiter' &&
          !['completed', 'cancelled'].includes(order.status) &&
          orderDate === today
        );
      });
      setWaiterOrders(waiterOrders);
    } catch (error) {
      console.error('Error loading waiter orders:', error);
    }
  };

  // Initialize data on component mount (only once)
  useEffect(() => {
    loadData();
    loadWaiters();
    if (!offlineMode) {
      loadTables();
      loadWaiterOrders();
    }
  }, [offlineMode]);

  // Apply auto discount when receipt items change and auto discount is enabled
  useEffect(() => {
    if (branchReceiptData?.enableAutoDiscount && receiptItems.length > 0) {
      const subtotal = receiptItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
      let autoDiscount = 0;
      
      if (branchReceiptData.discountType === 'percentage') {
        autoDiscount = subtotal * (branchReceiptData.discountValue / 100);
      } else {
        autoDiscount = Math.min(branchReceiptData.discountValue, subtotal);
      }
      
      setDiscount(autoDiscount.toFixed(2));
    } else if (branchReceiptData && !branchReceiptData.enableAutoDiscount && discount !== '0') {
      // Reset discount if auto discount is disabled
      setDiscount('0');
    }
  }, [receiptItems, branchReceiptData, setDiscount]);

  const inventoryById = useMemo(
    () => inventoryItems.reduce((map, item) => ({ ...map, [item.id]: item }), {}),
    [inventoryItems]
  );

  const categories = useMemo(() => {
    const unique = Array.from(new Set(products.map((p) => p.category)));
    return [{ label: 'All Categories', value: 'All' }, ...unique.map((value) => ({ label: value, value }))];
  }, [products]);

  const filteredProducts = useMemo(
    () => (category === 'All' ? products : products.filter((product) => product.category === category)),
    [category, products]
  );

  const getProductAvailability = (product, nextQuantity = 1) => {
    if (offlineMode) {
      return {
        available: true,
        statusText: 'Offline mode: availability deferred until sync'
      };
    }

    const shortages = [];
    const recipeItems = product.recipe || product.recipeDetails || [];

    recipeItems.forEach((recipeItem) => {
      const inventoryItem = inventoryById[recipeItem.inventoryId];
      const required = Number(recipeItem.quantity || 0) * nextQuantity;
      if (!inventoryItem) {
        shortages.push(`${recipeItem.name || 'Ingredient'} missing`);
      } else if (inventoryItem.stock <= 0) {
        shortages.push(`${inventoryItem.name} out of stock`);
      } else if (inventoryItem.stock < required) {
        shortages.push(`${inventoryItem.name} insufficient`);
      }
    });

    return {
      available: shortages.length === 0,
      statusText: shortages.length > 0 ? shortages.join(', ') : 'Ready'
    };
  };

  const selectedPendingData = useMemo(
    () => pendingOptions.find((option) => option.value === selectedPending),
    [pendingOptions, selectedPending]
  );

  const handleAddSelected = () => {
    if (selectedPendingData) {
      selectedPendingData.items.forEach((item) => addReceiptItem(item));
    }
  };

  const refreshTableData = async () => {
    if (offlineMode) return;
    try {
      const response = await fetchTables();
      if (response && response.tables) {
        setTables(response.tables);
      }
    } catch (error) {
      console.error('Unable to refresh table data:', error);
    }
  };

  const handleAddToTable = async (tableId) => {
    if (!tableId) {
      setSaleError('Select a table before transferring items.');
      return;
    }
    if (receiptItems.length === 0) {
      setSaleError('Add product items before transferring to a table.');
      return;
    }

    try {
      const subtotal = receiptItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
      const discountValue = 0;
      const total = subtotal - discountValue;

      if (offlineMode) {
        await offlineDb.createOfflineOrder({
          order_type: 'table',
          branch_id: user?.branch_id,
          user_id: user?.id || 1,
          table_id: tableId,
          action: 'pending',
          subtotal,
          tax_amount: 0,
          discount: discountValue,
          total,
          amount_paid: 0,
          change_given: 0,
          paid_at: null,
          payment_method: 'cash',
          notes: null,
          items: receiptItems.map((item) => ({
            product_id: item.product_id ?? item.id,
            product_variant_id: item.product_variant_id ?? null,
            quantity: item.quantity,
            unit_price: item.price,
            name: item.name
          }))
        });

        addReceiptItemsToTable(tableId, receiptItems);
        clearReceipt();
        setSaleMessage('Items transferred to table and saved offline successfully.');
        setSaleError('');
        return;
      }

      const response = await createOrder({
        order_type: 'table',
        branch_id: user?.branch_id,
        user_id: user?.id || 1,
        table_id: tableId,
        action: 'pending',
        subtotal,
        tax_amount: 0,
        discount: discountValue,
        total,
        amount_paid: 0,
        change_given: 0,
        paid_at: null,
        payment_method: 'cash',
        notes: null,
        items: receiptItems.map((item) => ({
          product_id: item.product_id ?? item.id,
          product_variant_id: item.product_variant_id ?? null,
          quantity: item.quantity,
          unit_price: item.price
        }))
      });

      addReceiptItemsToTable(tableId, receiptItems);
      clearReceipt();
      setSaleError('');
      await refreshTableData();

      let token = response.token;
      if (!token && response.order?.id) {
        try {
          const tokenResponse = await createToken(response.order.id);
          token = tokenResponse.token || tokenResponse;
        } catch (tokenError) {
          console.error('Failed to create token for table order:', tokenError);
        }
      }

      if (token) {
        setCreatedToken(token);
        setSaleMessage(`Items transferred to table successfully. Token #${token.token_no || token.tokenNumber || 'N/A'} issued.`);
        setPrintMode('token');
        const printOrderData = normalizeOrderData({
          id: response.order?.id || response.order?.order_id || null,
          order_number: response.order?.order_number || null,
          items: receiptItems,
          subtotal,
          discount: discountValue,
          total,
          order_type: 'table',
          tableId,
          token,
          receipt: null,
          createdAt: new Date().toLocaleString()
        });

        setCurrentPrintOrder(printOrderData);
        setTimeout(() => {
          handlePrint();
        }, 100);
      } else {
        setSaleMessage('Items transferred to table successfully.');
      }
    } catch (error) {
      setSaleError(error.message || 'Failed to transfer items to table.');
      console.error('Error transferring items to table:', error);
    }
  };

  const handlePrintWaiterOrder = async (selectedWaiterId) => {
    if (!selectedWaiterId) {
      setSaleError('Select a waiter before printing.');
      return;
    }
    if (receiptItems.length === 0) {
      setSaleError('Add product items before printing.');
      return;
    }

    const cashierId = user?.id || 1;
    const branchId = user?.branch_id || (selectedBranch?.id ? String(selectedBranch.id).replace(/^BR-/, '') : null);
    if (!cashierId || !branchId) {
      setSaleError('Unable to determine current user or branch for this order.');
      return;
    }

    try {
      setSaleError('');
      const subtotal = receiptItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
      const discountValue = Number(discount) || 0;
      const total = subtotal - discountValue;

      const waiterId = Number(selectedWaiterId);
    if (!Number.isInteger(waiterId)) {
      setSaleError('Please select a valid waiter.');
      return;
    }

    const orderData = {
        order_type: 'waiter',
        user_id: cashierId,
        waiter_id: waiterId,
        items: receiptItems.map(item => ({
          product_id: item.product_id ?? item.id,
          product_variant_id: item.product_variant_id ?? null,
          quantity: item.quantity,
          unit_price: item.price
        })),
        subtotal,
        discount: discountValue,
        total,
        amount_paid: 0,
        change_given: 0,
        paid_at: null,
        branch_id: branchId,
        payment_method: 'cash',
        action: 'print_both' // Completed waiter order and token generation
      };

      let createdOrder;
      let tokenGroups = [];

      if (offlineMode) {
        createdOrder = await offlineDb.createOfflineOrder({
          order_type: 'waiter',
          user_id: cashierId,
          waiter_id: waiterId,
          items: receiptItems.map((item) => ({
            product_id: item.product_id ?? item.id,
            product_variant_id: item.product_variant_id ?? null,
            quantity: item.quantity,
            unit_price: item.price,
            name: item.name
          })),
          subtotal,
          discount: discountValue,
          total,
          amount_paid: 0,
          change_given: 0,
          paid_at: null,
          branch_id: branchId,
          payment_method: 'cash',
          action: 'print_both'
        });
        addNewOrderToStore(createdOrder);
      } else {
        const response = await createOrder(orderData);
        createdOrder = response.order;
        const baseToken = response.token || (createdOrder?.token ? createdOrder.token : null);

        if (createdOrder && createdOrder.id) {
          const paperGroups = groupItemsByTokenPaper(receiptItems);
          const paperNumbers = Object.keys(paperGroups).sort((a, b) => Number(a) - Number(b));
          let firstToken = baseToken;

          for (let i = 0; i < paperNumbers.length; i += 1) {
            const paper = Number(paperNumbers[i]);
            try {
              let token = null;
              if (i === 0 && firstToken) {
                token = firstToken;
              } else {
                const tokenResp = await createToken(createdOrder.id);
                token = tokenResp?.token || tokenResp;
                if (i === 0 && !firstToken) {
                  firstToken = token;
                  createdOrder = { ...createdOrder, token };
                }
              }
              if (token) {
                tokenGroups.push({ ...token, paper, items: paperGroups[paper] });
              }
            } catch (tokenErr) {
              console.error('Failed to create token for waiter order paper', paper, tokenErr);
            }
          }

          if (tokenGroups.length === 0 && firstToken) {
            tokenGroups.push({ ...firstToken, paper: 1, items: receiptItems });
          }
        }

        if (createdOrder) {
          addNewOrderToStore(createdOrder);
        }
      }

      if (!createdOrder) {
        throw new Error('Unable to create waiter order.');
      }

      setPrintMode('both');
      const printOrderData = normalizeOrderData({
        id: createdOrder.id,
        order_number: createdOrder.order_number,
        items: receiptItems,
        subtotal,
        discount: discountValue,
        total,
        order_type: 'waiter',
        waiter_id: selectedWaiterId,
        token: tokenGroups[0] || createdOrder.token || (createdOrder.token_number ? { token_no: createdOrder.token_number } : null),
        receipt: createdOrder.receipt || null,
        createdAt: createdOrder.created_at || new Date().toLocaleString()
      });

      setCurrentPrintOrder({
        ...printOrderData,
        tokenGroups
      });
      setCreatedToken(tokenGroups[0] || createdOrder.token || (createdOrder.token_number ? { token_no: createdOrder.token_number } : null));
      setSaleMessage(`Waiter order created ${offlineMode ? 'offline ' : ''}successfully. Order No: ${createdOrder.order_number || createdOrder.id}${tokenGroups.length > 0 ? `, Tokens: ${tokenGroups.length}` : ''}`);

      clearReceipt();

      setTimeout(() => {
        handlePrint();
      }, 100);

      loadWaiters();
    } catch (error) {
      setSaleError(error.message || 'Failed to create waiter order.');
      console.error('Error creating waiter order:', error);
    }
  };

  const handleAddPendingWaiterOrder = async (selectedWaiterId) => {
    if (!selectedWaiterId) {
      setSaleError('Select a waiter before creating order.');
      return;
    }
    if (receiptItems.length === 0) {
      setSaleError('Add product items before creating order.');
      return;
    }

    const cashierId = user?.id || 1;
    const branchId = user?.branch_id || (selectedBranch?.id ? String(selectedBranch.id).replace(/^BR-/, '') : null);
    if (!cashierId || !branchId) {
      setSaleError('Unable to determine current user or branch for this order.');
      return;
    }

    try {
      setSaleError('');
      const subtotal = receiptItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
      const discountValue = Number(discount) || 0;
      const total = subtotal - discountValue;

      const waiterId = Number(selectedWaiterId);
      if (!Number.isInteger(waiterId)) {
        setSaleError('Please select a valid waiter.');
        return;
      }

      const orderData = {
        order_type: 'waiter',
        user_id: cashierId,
        waiter_id: waiterId,
        items: receiptItems.map(item => ({
          product_id: item.product_id ?? item.id,
          product_variant_id: item.product_variant_id ?? null,
          quantity: item.quantity,
          unit_price: item.price
        })),
        subtotal,
        discount: discountValue,
        total,
        amount_paid: 0,
        change_given: 0,
        paid_at: null,
        branch_id: branchId,
        payment_method: 'cash',
        action: 'pending' // Create waiter order as pending, then issue token separately
      };

      const response = await createOrder(orderData);
      let createdOrder = response.order;
      let tokenGroups = [];

      if (createdOrder && createdOrder.id) {
        const paperGroups = groupItemsByTokenPaper(receiptItems);
        const paperNumbers = Object.keys(paperGroups).sort((a, b) => Number(a) - Number(b));
        const baseToken = response.token || (createdOrder.token ? createdOrder.token : null);
        let firstToken = baseToken;

        for (let i = 0; i < paperNumbers.length; i += 1) {
          const paper = Number(paperNumbers[i]);
          try {
            let token = null;

            if (i === 0 && firstToken) {
              token = firstToken;
            } else {
              const tokenResp = await createToken(createdOrder.id);
              token = tokenResp?.token || tokenResp;
              if (i === 0 && !firstToken) {
                firstToken = token;
                createdOrder = { ...createdOrder, token };
              }
            }

            if (token) {
              tokenGroups.push({ ...token, paper, items: paperGroups[paper] });
            }
          } catch (tokenErr) {
            console.error('Failed to create token for waiter order paper', paper, tokenErr);
          }
        }

        if (tokenGroups.length === 0 && firstToken) {
          tokenGroups.push({ ...firstToken, paper: 1, items: receiptItems });
        }
      }

      // Add new order to store
      if (createdOrder) {
        addNewOrderToStore(createdOrder);
        setSelectedWaiterOrderId(String(createdOrder.id));
      }

      // Set print mode to token only and trigger print
      setPrintMode('token');
      const printOrderData = normalizeOrderData({
        id: createdOrder.id,
        order_number: createdOrder.order_number,
        items: receiptItems,
        subtotal,
        discount: discountValue,
        total,
        order_type: 'waiter',
        waiter_id: selectedWaiterId,
        token: tokenGroups[0] || createdOrder.token || null,
        receipt: createdOrder.receipt || null,
        createdAt: createdOrder.created_at || new Date().toLocaleString()
      });

      setCurrentPrintOrder({
        ...printOrderData,
        tokenGroups
      });
      setCreatedToken(tokenGroups[0] || createdOrder.token || null);
      setSaleMessage(`Waiter order created successfully. Order No: ${createdOrder.order_number || createdOrder.id}${tokenGroups.length > 0 ? `, Tokens: ${tokenGroups.length}` : ''}`);

      // Clear receipt after creating order
      clearReceipt();

      // Trigger print after a short delay
      setTimeout(() => {
        handlePrint();
      }, 100);

      // Refresh waiters and waiter orders to ensure latest data
      await loadWaiters();
      await loadWaiterOrders();
    } catch (error) {
      setSaleError(error.message || 'Failed to create waiter order.');
      console.error('Error creating waiter order:', error);
    }
  };

  const handleAddToWaiterOrder = async (orderId) => {
    if (!orderId) {
      setSaleError('Select an existing waiter order before adding items.');
      return;
    }
    if (receiptItems.length === 0) {
      setSaleError('Add product items before adding to a waiter order.');
      return;
    }

    const waiterOrder = waiterOrders.find((order) => String(order.id) === String(orderId));
    if (!waiterOrder) {
      setSaleError('Selected waiter order could not be found.');
      return;
    }

    try {
      setSaleError('');
      const response = await appendOrderItems(orderId, receiptItems.map((item) => ({
        product_id: item.product_id ?? item.id,
        product_variant_id: item.product_variant_id ?? null,
        quantity: item.quantity,
        unit_price: item.price
      })), 'print_both');

      if (response.order) {
        addNewOrderToStore(response.order);
      }

      const paperGroups = groupItemsByTokenPaper(receiptItems);
      const paperNumbers = Object.keys(paperGroups).sort((a, b) => Number(a) - Number(b));
      let tokenGroups = [];
      let baseToken = response.token || null;

      for (let i = 0; i < paperNumbers.length; i += 1) {
        const paper = Number(paperNumbers[i]);
        try {
          let token = null;

          if (i === 0 && baseToken) {
            token = baseToken;
          } else {
            const tokenResp = await createToken(orderId);
            token = tokenResp?.token || tokenResp;
            if (i === 0 && !baseToken) {
              baseToken = token;
            }
          }

          if (token) {
            tokenGroups.push({ ...token, paper, items: paperGroups[paper] });
          }
        } catch (tokenErr) {
          console.error('Failed to create token for paper', paper, tokenErr);
        }
      }

      if (tokenGroups.length === 0 && baseToken) {
        tokenGroups.push({ ...baseToken, paper: 1, items: receiptItems });
      }

      const appendedSubtotal = receiptItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
      const printOrderData = normalizeOrderData({
        id: orderId,
        order_number: waiterOrder.order_number,
        items: receiptItems,
        subtotal: appendedSubtotal,
        discount: 0,
        total: appendedSubtotal,
        order_type: 'waiter',
        waiter_id: selectedWaiterId,
        token: tokenGroups[0] || response.token || null,
        receipt: null,
        createdAt: new Date().toLocaleString()
      });

      setSaleMessage(`Added items to waiter order #${orderId} successfully. Tokens issued: ${tokenGroups.length}`);
      setCreatedToken(tokenGroups[0] || response.token || null);
      setPrintMode('token');
      setCurrentPrintOrder({
        ...printOrderData,
        tokenGroups
      });
      clearReceipt();
      await loadWaiterOrders();

      setTimeout(() => {
        handlePrint();
      }, 100);
    } catch (error) {
      setSaleError(error.message || 'Failed to add items to waiter order.');
      console.error('Error appending items to waiter order:', error);
    }
  };

  const subtotal = receiptItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const discountValue = Math.min(Number(discount) || 0, subtotal);
  const grandTotal = subtotal - discountValue;
  const balance = Number(receivedCash || 0) - grandTotal;

  const refreshData = async () => {
    try {
      // Force refresh inventory after a sale
      await initializeInventory(fetchInventory, true);
    } catch (error) {
      setSaleError(error.message || 'Unable to refresh inventory.');
    }
  };

  const waiterOrderOptions = useMemo(() => {
    const filtered = waiterOrders.filter((order) => {
      if (!selectedWaiterId) return true;
      return String(order.waiter_id) === String(selectedWaiterId);
    });

    return filtered.map((order) => ({
      value: String(order.id),
      label: `#${order.order_number || order.id} • ${order.status || 'pending'} • ${waiters.find((waiter) => waiter.id === order.waiter_id)?.name || `Waiter ${order.waiter_id}`}`
    }));
  }, [waiterOrders, selectedWaiterId, waiters]);

  const completeTableOrder = async (orderId) => {
    try {
      if (!orderId) return;
      await completeOrder(orderId);
      await refreshTableData();
    } catch (error) {
      console.error('Error completing table order:', error);
    }
  };

  const normalizeOrderForStore = (order) => {
    if (!order || !order.id) return order;

    return {
      ...order,
      order_number: order.order_number ?? order.orderId ?? order.id,
      token_number: order.token_number || order.token?.token_no || null,
      cashier_name: order.cashier_name || user?.name || order.user_name || null,
      status: order.status || 'pending',
      created_at: order.created_at || new Date().toISOString()
    };
  };

  // Helper function to add new order to store
  const addNewOrderToStore = (newOrder) => {
    const normalizedOrder = normalizeOrderForStore(newOrder);
    if (normalizedOrder && normalizedOrder.id) {
      addOrder(normalizedOrder);
    }
  };

  const validateReceiptAvailability = () => {
    if (offlineMode) {
      return null;
    }

    const unavailable = receiptItems.map((item) => {
      const product = products.find((p) => p.id === item.id || p.id === item.product_id);
      const availability = product
        ? getProductAvailability(product, item.quantity)
        : {
            available: false,
            statusText: `${item.name || 'Item'} recipe unavailable`
          };
      return { item, availability, product };
    }).filter(({ availability }) => !availability.available);

    if (unavailable.length > 0) {
      return unavailable
        .map(({ item, availability, product }) => {
          const label = item.name || product?.name || 'Item';
          return `${label}: ${availability.statusText}`;
        })
        .join('; ');
    }
    return null;
  };

  const handleSubmitSale = async (action = 'save', paymentMethod = 'cash') => {
    if (isSubmitting) {
      return; // Prevent duplicate submissions
    }

    if (receiptItems.length === 0) {
      setSaleError('Add at least one item before submitting the sale.');
      return;
    }

    if (!paymentMethod) {
      setSaleError('Please select a payment method before submitting.');
      return;
    }

    if (action === 'print_bill' || action === 'print_both' || action === 'print_only') {
      const availabilityError = validateReceiptAvailability();
      if (availabilityError) {
        setSaleError(`Cannot print due to inventory shortage: ${availabilityError}`);
        return;
      }
    }

    setSaleError('');
    const actionMap = {
      'Walk-in': 'walkin',
      Tables: 'table',
      Waiters: 'waiter'
    };
    const orderType = actionMap[activeTab] || 'walkin';
    const selectedTableId = orderType === 'table' ? selectedTable || null : null;

    try {
      if (isPrintPending) {
        setSaleError('Please complete or cancel the print dialog before submitting another order.');
        return;
      }
      setIsSubmitting(true);
      let response;
      if (offlineMode) {
        const offlineOrder = await offlineDb.createOfflineOrder({
          action,
          branch_id: user?.branch_id,
          user_id: user?.id || 1,
          order_type: orderType,
          table_id: orderType === 'table' ? selectedTableId : null,
          waiter_id: orderType === 'waiter' ? (waiterId ? parseInt(waiterId) : null) : null,
          subtotal,
          tax_amount: 0,
          discount: discountValue,
          total: grandTotal,
          amount_paid: Number(receivedCash || 0),
          change_given: balance,
          paid_at: Number(receivedCash || 0) > 0 ? new Date().toISOString() : null,
          payment_method: paymentMethod,
          notes: null,
          items: receiptItems.map((item) => ({ product_id: item.product_id ?? item.id, quantity: item.quantity, unit_price: item.price, name: item.name }))
        });

        response = { message: 'Order saved offline.', order: offlineOrder };
      } else {
        response = await submitSale({
          action,
          branch_id: user?.branch_id,
          user_id: user?.id || 1,
          order_type: orderType,
          table_id: orderType === 'table' ? selectedTableId : null,
          waiter_id: orderType === 'waiter' ? (waiterId ? parseInt(waiterId) : null) : null,
          subtotal,
          tax_amount: 0,
          discount: discountValue,
          total: grandTotal,
          amount_paid: Number(receivedCash || 0),
          change_given: balance,
          paid_at: Number(receivedCash || 0) > 0 ? new Date().toISOString() : null,
          payment_method: paymentMethod,
          notes: null,
          items: receiptItems.map((item) => ({ product_id: item.product_id ?? item.id, quantity: item.quantity, unit_price: item.price }))
        });
      }

      const paperGroups = groupItemsByTokenPaper(receiptItems);
      const paperNumbers = Object.keys(paperGroups).map(Number).sort((a, b) => a - b);
      const tokenGroups = [];
      const orderId = response.order?.id || response.order?.order_id || null;

      const baseToken = response.order?.token || (response.order?.token_number ? { token_no: response.order.token_number } : null);
      if (paperNumbers.length > 0) {
        const firstPaper = paperNumbers[0];
        if (baseToken) {
          tokenGroups.push({ ...baseToken, paper: firstPaper, items: paperGroups[firstPaper] });
        } else if (orderId && action !== 'print_only') {
          const tokenResponse = await createToken(orderId);
          const token = tokenResponse.token || tokenResponse;
          tokenGroups.push({ ...token, paper: firstPaper, items: paperGroups[firstPaper] });
        }

        for (let i = 1; i < paperNumbers.length; i += 1) {
          const paper = paperNumbers[i];
          if (orderId && action !== 'print_only') {
            try {
              const tokenResponse = await createToken(orderId);
              const token = tokenResponse.token || tokenResponse;
              tokenGroups.push({ ...token, paper, items: paperGroups[paper] });
            } catch (tokenError) {
              console.error('Failed to create token for paper', paper, tokenError);
            }
          }
        }
      }

      const successMessage = response.message || 'Order processed successfully.';
      const tokenMessage = tokenGroups.length > 0
        ? ` ${tokenGroups.length} token${tokenGroups.length === 1 ? '' : 's'} issued.`
        : baseToken ? ` Token #${baseToken.token_no || baseToken.token_number} issued.` : '';
      setSaleMessage(`${successMessage}${tokenMessage}`);
      setCreatedToken(tokenGroups[0] || baseToken || null);

      if (response.order) {
        addNewOrderToStore(response.order);
      }

      if (action === 'print_bill' || action === 'print_both' || action === 'print_only') {
        const printModeToUse = action === 'print_both' ? 'both' : 'receipt';
        triggerPrint(
          receiptItems,
          subtotal,
          discountValue,
          grandTotal,
          selectedTableId,
          tokenGroups[0] || baseToken || null,
          tokenGroups,
          response.order?.receipt || null,
          orderType,
          printModeToUse,
          orderId,
          response.order?.order_number || null
        );
        if (action === 'print_only') {
          clearReceipt();
          if (!offlineMode) refreshData();
        }
      } else {
        clearReceipt();
        if (!offlineMode) refreshData();
      }
    } catch (error) {
      const details = error.payload?.details ? `: ${error.payload.details.join('; ')}` : '';
      setSaleError(error.message ? `${error.message}${details}` : `Order failed${details}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateOrder = async () => {
    if (isSubmitting || isPrintPending) {
      if (isPrintPending) {
        setSaleError('Please complete or cancel the print dialog before updating the order.');
      }
      return; // Prevent duplicate submissions or new requests while printing is pending
    }

    if (receiptItems.length === 0) {
      setSaleError('Add at least one item before updating the order.');
      return;
    }

    if (!editingOrder || !editingOrder.id) {
      setSaleError('No order selected for editing.');
      return;
    }

    try {
      setIsSubmitting(true);
      setSaleError('');
      
      // Prepare items for update
      const itemsToUpdate = receiptItems.map((item) => ({
        product_id: item.product_id ?? item.id,
        quantity: item.quantity,
        unit_price: item.price
      }));

      // Update the order items using the new update endpoint
      const response = await updateOrderItems(editingOrder.id, itemsToUpdate);

      setSaleMessage('Order updated successfully.');
      
      // Clear the editing state
      clearEditingOrder();
      clearReceipt();

      // Refresh the orders if not in offline mode
      if (!offlineMode) {
        refreshData();
      }
    } catch (error) {
      const details = error.payload?.details ? `: ${error.payload.details.join('; ')}` : '';
      setSaleError(error.message ? `${error.message}${details}` : 'Failed to update order.');
    } finally {
      setIsSubmitting(false);
    }
  };
  const triggerPrint = (items, subtotal, discount, total, tableId, token, tokenGroups = [], receipt, order_type, mode, orderId = null, order_number = null, completeAfterPrint = false) => {
    const orderData = normalizeOrderData({
      id: orderId,
      order_number,
      items,
      subtotal,
      discount,
      total,
      tableId,
      token,
      receipt,
      order_type,
      createdAt: new Date().toLocaleString()
    }); 

    // Update state with print data
    setCurrentPrintOrder({
      ...orderData,
      tokenGroups,
      completeAfterPrint
    });
    setIsPrintPending(true);

    // Trigger print after DOM has updated (after state updates are rendered)
    setTimeout(() => {
      setIsPrinting(true);
      handlePrint();
    }, 100);
  };

  const handlePrintBill = (options = {}) => {
    setPrintMode('both');
    handleSubmitSale('print_bill', options.paymentMethod);
  };

  const handlePrintOnly = (options = {}) => {
    setPrintMode('receipt');
    handleSubmitSale('print_only', options.paymentMethod);
  };

  const handleSave = (options = {}) => {
    handleSubmitSale('save', options.paymentMethod);
  };

  /**
   * Clear the current receipt
   */
  const handleClear = () => {
    clearReceipt();
  };

  const updatePrintMode = (mode) => {
    if (['receipt', 'token', 'both'].includes(mode)) {
      setPrintMode(mode);
    }
  };

  /**
   * onPrint callback - allows child components to trigger printing for a specific order
   * Centralizes all print logic in the parent component
   */
  const onPrint = async (order, mode = printMode, completeAfterPrint = false) => {
    if (!order) {
      setSaleError('No order selected for printing.');
      return;
    }

    const orderToPrint = await loadOrderPrintData(order);
    const items = Array.isArray(orderToPrint.items) ? orderToPrint.items : [];
    if (items.length === 0) {
      setSaleError('No items to print.');
      return;
    }

    triggerPrint(
      items,
      orderToPrint.subtotal || 0,
      orderToPrint.discount || 0,
      orderToPrint.total || items.reduce((sum, item) => sum + (Number(item.price) || 0) * (Number(item.quantity) || 0), 0),
      orderToPrint.tableId || orderToPrint.table?.id || (orderToPrint.order_type === 'table' ? selectedTable : null),
      orderToPrint.tableName || orderToPrint.name || null,
      orderToPrint.token || (orderToPrint.token_number ? { token_no: orderToPrint.token_number } : null),
      orderToPrint.receipt || null,
      orderToPrint.order_type || null,
      mode,
      orderToPrint.order_id || orderToPrint.id || null,
      orderToPrint.order_number || null,
      completeAfterPrint
    );
  };

  return (
    <div>
      {editingOrder && (
        <div className="mb-4 rounded-2xl bg-blue-50 border-l-4 border-blue-600 p-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-blue-900">Editing Order</h3>
              <p className="text-sm text-blue-700">Order #ORD-{editingOrder.order_number || editingOrder.id} | Total: PKR {Number(editingOrder.total || 0).toFixed(2)}</p>
            </div>
            <button
              onClick={() => {
                clearEditingOrder();
                clearReceipt();
              }}
              className="text-blue-600 hover:text-blue-900 font-semibold"
            >
              ✕ Cancel Edit
            </button>
          </div>
        </div>
      )}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between mb-2 pt-6">
        <div>
          <h1 className="text-3xl font-bold">Sales</h1>
        </div>
        <div className="flex flex-wrap gap-3">
          {['Walk-in', 'Tables', 'Waiters'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`rounded-full px-4 py-2 text-sm font-semibold transition ${activeTab === tab
                ? 'bg-blue-600 text-white shadow-lg'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      <div className="">
        {activeTab === 'Walk-in' ? (
          <div className="grid grid-cols-1 xl:grid-cols-[1.2fr_0.95fr] gap-6" >
            <div className="xl:col-span-2">
              <WalkInSection
                categories={categories}
                filteredProducts={filteredProducts}
                addReceiptItem={addReceiptItem}
                getProductAvailability={getProductAvailability}
                pendingOptions={pendingOptions}
                handleAddSelected={handleAddSelected}
                onTransferToTable={handleAddToTable}
                handleSubmitSale={handleSubmitSale}
                handleSave={handleSave}
                handlePrintBill={handlePrintBill}
                handlePrintOnly={handlePrintOnly}
                handleClear={handleClear}
                saleError={saleError}
                saleMessage={saleMessage}
                printMode={printMode}
                setPrintMode={setPrintMode}
                waiters={waiters}
                selectedWaiterId={selectedWaiterId}
                onChangeWaiter={setSelectedWaiterId}
                waiterOrderOptions={waiterOrderOptions}
                selectedWaiterOrderId={selectedWaiterOrderId}
                onChangeWaiterOrder={setSelectedWaiterOrderId}
                onPrintWaiterOrder={handlePrintWaiterOrder}
                onAddPendingWaiterOrder={handleAddPendingWaiterOrder}
                onAddToWaiterOrder={handleAddToWaiterOrder}
                branchReceiptData={branchReceiptData}
                editingOrder={editingOrder}
                handleUpdateOrder={handleUpdateOrder}
                isSubmitting={isSubmitting}
                isPrintPending={isPrintPending}
              />
            </div>
          </div>
        ) : (
          <>
            <div className="space-y-4">
              {activeTab === 'Tables' ? (
                <TablesSection
                  selectedTable={selectedTable}
                  onCreateTable={createTable}
                  onActivateTable={activateTable}
                  onSelectTable={setSelectedTable}
                  onPrintTable={onPrint}
                  onCancelTable={cancelTableOrder}
                  onRemoveTable={removeTable}
                  products={filteredProducts}
                  addReceiptItem={addReceiptItem}
                  getProductAvailability={getProductAvailability}
                  updatePrintMode={updatePrintMode}
                />
              ) : (
                <SalesWaiterSection
                  waiterId={waiterId}
                  onChangeWaiter={(e) => setWaiterId(e.target.value)}
                />
              )}
            </div>
          </>
        )}
      </div>

      {/* Offscreen print container using absolute positioning */}
      <div
        style={{
          position: 'absolute',
          left: '-10000px',
          top: '-10000px',
          width: '80mm',
          height: 'auto',
          visibility: 'hidden'
        }}
      >
        <PrintContent
          ref={printRef}
          printMode={printMode}
          order={currentPrintOrder}
        />
      </div>
    </div>
  );
};

export default Sales;
